// Financial Reporting tab — what the chips and the attention strip count.
//
// Pure derivation, deliberately separated from the components that render it so
// each number can be asserted without mounting a tab. Every count here answers
// a question a reader asks out loud ("how many are waiting on me?"), and each
// one is derived from the field that actually carries that fact.
//
// 🔴 THE FIELD THAT ACTUALLY CARRIES IT. `ReportRequestItemStatuses.Submitted`
// and `UnderReview` have NO production writer — nothing in the codebase ever
// moves an item into either — so an item the borrower HAS submitted still reads
// `status === "pending"`. Deriving "has the borrower sent this?" from `status`
// therefore counts zero, always, and looks like a quiet portfolio rather than a
// broken read. `awaitingReview` is server-populated from the live staged
// submission (the same row File / Approve / Return act on), so it is the only
// honest source for that question and the only one used below.

import {
  TERMINAL_REQUEST_STATUSES,
  type ReportRequestDto,
  type ReportRequestItemDto,
  type ReportRequestType,
} from "@/api/portfolioAdmin/reportRequestsApi";
import { isReportRequestOverdue } from "../defaults/timeline";

/** Left-to-right chip order. All four ALWAYS render — see `openCountByType`. */
export const REPORT_REQUEST_TYPE_CHIP_ORDER: readonly ReportRequestType[] = [
  "Quarterly",
  "Annual",
  "DscrWatchMonthly",
  "DefaultWorkout",
];

/**
 * Still live: not cancelled, not reported. A `complete` request is deliberately
 * still open — its items are in, but the report has not been filed, so there is
 * something left to do on it.
 */
export function isOpenRequest(request: ReportRequestDto): boolean {
  return !TERMINAL_REQUEST_STATUSES.includes(request.status);
}

export function requestsOfType(
  requests: readonly ReportRequestDto[],
  type: ReportRequestType,
): ReportRequestDto[] {
  return requests.filter((r) => r.requestType === type);
}

/**
 * Open requests per type, for EVERY type — including the ones sitting at zero.
 *
 * 🔴 A measured zero is information: "no Annual request is open" is a fact a
 * reader can act on, and it is not the same statement as an absent chip, which
 * reads as "not loaded" or "not applicable here". Returning a complete record
 * rather than a `groupBy` of what happens to be present is what makes the four
 * chips constant furniture instead of a list that changes shape underneath
 * somebody mid-click.
 */
export function openCountByType(
  requests: readonly ReportRequestDto[],
): Record<ReportRequestType, number> {
  const counts = {
    Quarterly: 0,
    Annual: 0,
    DscrWatchMonthly: 0,
    DefaultWorkout: 0,
  } as Record<ReportRequestType, number>;
  for (const request of requests) {
    if (!isOpenRequest(request)) continue;
    if (request.requestType in counts) counts[request.requestType] += 1;
  }
  return counts;
}

/** The leftmost type with something open; Quarterly when everything is quiet. */
export function defaultSelectedType(
  counts: Record<ReportRequestType, number>,
): ReportRequestType {
  return REPORT_REQUEST_TYPE_CHIP_ORDER.find((t) => counts[t] > 0) ?? "Quarterly";
}

export interface AwaitingReviewRow {
  request: ReportRequestDto;
  item: ReportRequestItemDto;
}

/**
 * Items whose current submission is sitting unreviewed.
 *
 * Read off `awaitingReview` — NOT `status === "submitted"`, which no writer
 * ever sets (see the header). Scoped to open requests: a cancelled request's
 * leftovers are nobody's queue.
 */
export function awaitingReviewRows(
  requests: readonly ReportRequestDto[],
): AwaitingReviewRow[] {
  const rows: AwaitingReviewRow[] = [];
  for (const request of requests) {
    if (!isOpenRequest(request)) continue;
    for (const item of request.items) {
      if (item.awaitingReview) rows.push({ request, item });
    }
  }
  return rows;
}

/** Whether the borrower has ever sent anything for this item — again from the
 *  staged submission, never from `status`. */
export function hasSubmission(item: ReportRequestItemDto): boolean {
  return item.stagedSubmissionId !== null;
}

/**
 * Ready for its report. Server-computed (`canGenerateReport` = every item
 * approved, or complete-with-exceptions, and nothing filed yet) and read rather
 * than re-derived, so this count can never offer what the endpoint refuses.
 */
export function readyToReportRequests(
  requests: readonly ReportRequestDto[],
): ReportRequestDto[] {
  return requests.filter((r) => r.canGenerateReport);
}

/**
 * Past its due date and still actionable — the single shared overdue rule.
 *
 * `tz` is a parameter rather than a hook because this is a pure function; the
 * component that calls it holds the viewer's zone and passes it down. Omitted,
 * it inherits `isReportRequestOverdue`'s America/Phoenix fallback.
 */
export function overdueRequests(
  requests: readonly ReportRequestDto[],
  now: Date = new Date(),
): ReportRequestDto[] {
  return requests.filter((r) => isReportRequestOverdue(r, now));
}

/**
 * The request already covering a suggested period, if one exists.
 *
 * Compared as WALL-CALENDAR dates for the same reason everything else on this
 * surface is: `periodStart` is a date, not an instant. Cancelled requests do
 * not count — that period is genuinely free to be re-issued.
 */
export function requestForPeriod(
  requests: readonly ReportRequestDto[],
  type: ReportRequestType,
  suggestedPeriodStart: string,
  suggestedPeriodEnd: string,
): ReportRequestDto | null {
  const start = suggestedPeriodStart.slice(0, 10);
  const end = suggestedPeriodEnd.slice(0, 10);
  return (
    requestsOfType(requests, type).find(
      (r) =>
        r.status !== "cancelled" &&
        r.periodStart.slice(0, 10) === start &&
        r.periodEnd.slice(0, 10) === end,
    ) ?? null
  );
}

/** Newest first, by the instant it was issued. */
export function byNewestIssued(
  requests: readonly ReportRequestDto[],
): ReportRequestDto[] {
  return [...requests].sort((a, b) => b.issuedAt.localeCompare(a.issuedAt));
}

// --- Lifecycle stage ---------------------------------------------------------

export type LifecycleStage = "request" | "review" | "report";

export const LIFECYCLE_STAGES: readonly LifecycleStage[] = [
  "request",
  "review",
  "report",
];

/**
 * Where a request stands, as the three things that happen to it: it is asked
 * for, what comes back is reviewed, and a report is written and filed.
 *
 * `complete` marks the whole chain done — the report is filed and there is
 * nothing further. A cancelled request stops at review with `complete` false;
 * it did not finish, it was called off, and its own status badge says so.
 */
export function lifecycleStage(request: ReportRequestDto): {
  stage: LifecycleStage;
  index: number;
  complete: boolean;
} {
  const filed = request.filedDocumentId !== null;
  if (filed) return { stage: "report", index: 2, complete: true };
  const reviewDone = request.canGenerateReport || request.generatedReportId !== null;
  if (reviewDone && request.status !== "cancelled") {
    return { stage: "report", index: 2, complete: false };
  }
  return { stage: "review", index: 1, complete: false };
}
